/**
 * ScholarSync — Forgot Password Page
 * File: frontend/src/ForgotPassword.js
 *
 * User enters email → backend sends reset link via SendGrid.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from './config';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Something went wrong');
      }

      setSubmitted(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#F3F4F6',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      padding: '20px',
    }}>
      <div style={{
        background: '#fff',
        borderRadius: 12,
        padding: '40px 36px',
        maxWidth: 420,
        width: '100%',
        boxShadow: '0 4px 15px rgba(0,0,0,0.08)',
      }}>
        {submitted ? (
          <>
            <h2 style={{ margin: '0 0 12px', color: '#1a1a2e', textAlign: 'center' }}>
              Check your email
            </h2>
            <p style={{ color: '#555', textAlign: 'center', lineHeight: 1.6 }}>
              If an account exists for <strong>{email}</strong>, we've sent a password reset link.
              Check your inbox and click the link to reset your password.
            </p>
            <p style={{ color: '#888', textAlign: 'center', fontSize: 14, marginTop: 16 }}>
              Didn't receive it? Check your spam folder or{' '}
              <span
                onClick={() => { setSubmitted(false); setEmail(''); }}
                style={{ color: '#4a6cf7', cursor: 'pointer' }}
              >
                try again
              </span>.
            </p>
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Link to="/login" style={{ color: '#4a6cf7', textDecoration: 'none', fontSize: 14 }}>
                ← Back to Login
              </Link>
            </div>
          </>
        ) : (
          <>
            <h2 style={{ margin: '0 0 8px', color: '#1a1a2e', textAlign: 'center' }}>
              Reset your password
            </h2>
            <p style={{ color: '#666', textAlign: 'center', marginBottom: 24, fontSize: 14 }}>
              Enter your email and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleSubmit}>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 14, color: '#333', fontWeight: 500 }}>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  fontSize: 15,
                  marginBottom: 20,
                  boxSizing: 'border-box',
                }}
              />

              {error && (
                <p style={{ color: '#e53e3e', fontSize: 14, marginBottom: 16 }}>{error}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: '#4a6cf7',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  fontSize: 16,
                  fontWeight: 600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.7 : 1,
                }}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>

            <div style={{ textAlign: 'center', marginTop: 20 }}>
              <Link to="/login" style={{ color: '#4a6cf7', textDecoration: 'none', fontSize: 14 }}>
                ← Back to Login
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}